import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Site, ProcessedPayout } from '@/types';
import { apiService } from '@/utils/api';

const VismaProcessedPayouts: React.FC = () => {
  const [filters, setFilters] = useState({
    dateCreatedFrom: '',
    dateCreatedTo: '',
    status: '',
    paymentReference: '',
  });
  
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState<{ objects: ProcessedPayout[], page: number, total: number } | null>(null);

  const [siteId, setSiteId] = useState<number | null>(null);

  // Fetch sites
  const { data: sites = [] } = useQuery<Site[]>('sites', apiService.getSites);

  // Auto-select first site when sites are loaded
  useEffect(() => {
    if (sites.length > 0 && !siteId) {
      setSiteId(sites[0].id);
    }
  }, [sites, siteId]);

  // Helper function to convert camelCase to snake_case for API parameters
  const convertToApiParams = (filters: any) => {
    const apiParams: Record<string, any> = {};
    
    if (filters.dateCreatedFrom) {
      apiParams.date_created_from = filters.dateCreatedFrom;
    }
    if (filters.dateCreatedTo) {
      apiParams.date_created_to = filters.dateCreatedTo;
    }
    if (filters.status) {
      apiParams.status = filters.status;
    }
    if (filters.paymentReference) {
      apiParams.payout_id = filters.paymentReference;
    }
    
    return apiParams;
  };


  // Fetch processed payouts
  const { data: payoutsData, isLoading } = useQuery(
    ['vismaProcessedPayouts', siteId, sortBy, sortOrder, currentPage],
    () => apiService.getVismaProcessedPayouts(siteId!, { order_by: sortBy, order_type: sortOrder, page: currentPage }),
    {
      enabled: !!siteId && !isSearchActive,
    }
  );

  const statusOptions = [
    { value: '', text: 'All Statuses' },
    { value: 'completed', text: 'Completed' },
    { value: 'error', text: 'Error' },
  ];

  const fields = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'payout_id', label: 'Payout ID', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'amount', label: 'Amount', sortable: true },
    { key: 'created_at', label: 'Created At', sortable: true },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = async () => {
    if (!siteId) return;
    
    const apiParams = convertToApiParams(filters);
    
    try {
      const results = await apiService.searchVismaProcessedPayouts(siteId, apiParams);
      setSearchResults(results);
      setIsSearchActive(true);
      setCurrentPage(1); // Reset to first page on new search
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page on sort
    setIsSearchActive(false);
    setSearchResults(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setIsSearchActive(false);
    setSearchResults(null);
  };

  const clearSearch = () => {
    setFilters({
      dateCreatedFrom: '',
      dateCreatedTo: '',
      status: '',
      paymentReference: '',
    });
    setIsSearchActive(false);
    setSearchResults(null);
    setCurrentPage(1);
  };

  // Determine which data to display
  const displayData = isSearchActive && searchResults ? (searchResults.objects || []) : (payoutsData?.objects || []);
  const totalCount = isSearchActive && searchResults ? searchResults.total : payoutsData?.total || 0;
  const totalPages = Math.ceil(totalCount / 20); // Assuming 20 items per page

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Visma Processed Payouts</h1>
      
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="dateCreatedFrom" className="block text-sm font-medium text-gray-700 mb-2">
              From Date
            </label>
            <input
              type="date"
              id="dateCreatedFrom"
              name="dateCreatedFrom"
              value={filters.dateCreatedFrom}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label htmlFor="dateCreatedTo" className="block text-sm font-medium text-gray-700 mb-2">
              To Date
            </label>
            <input
              type="date"
              id="dateCreatedTo"
              name="dateCreatedTo"
              value={filters.dateCreatedTo}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="paymentReference" className="block text-sm font-medium text-gray-700 mb-2">
              Payment Reference
            </label>
            <input
              type="text"
              id="paymentReference"
              name="paymentReference"
              value={filters.paymentReference}
              onChange={handleInputChange}
              placeholder="Payment Reference"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        
        <div className="flex space-x-4">
          <button
            onClick={handleSearch}
            className="text-white px-6 py-2 rounded-md hover:opacity-90 focus:outline-none focus:ring-2"
            style={{ backgroundColor: 'hsl(15 100% 52%)' }}
          >
            Search
          </button>
          <button
            onClick={clearSearch}
            className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Clear Search
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {fields.map(field => (
                  <th
                    key={field.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort(field.key)}
                  >
                    {field.label}
                    {sortBy === field.key && (
                      <span className="ml-1">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayData?.map((payout: ProcessedPayout) => (
                <tr key={payout.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payout.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payout.payout_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      payout.status === 'completed' ? 'bg-green-100 text-green-800' :
                      payout.status === 'error' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {payout.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payout.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payout.created_at ? new Date(payout.created_at).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {(!displayData || displayData.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            No payouts found
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isSearchActive && totalPages > 1 && (
        <div className="mt-6 flex justify-center">
          <nav className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === page
                      ? 'text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                  style={currentPage === page ? { backgroundColor: 'hsl(15 100% 52%)' } : {}}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </nav>
        </div>
      )}
      
      <p className="mt-3 text-sm text-gray-600">
        {isSearchActive ? `Search Results: ${totalCount} payouts found` : `Page ${currentPage} of ${totalPages} (${totalCount} total payouts)`}
      </p>
    </div>
  );
};

export default VismaProcessedPayouts;
